import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import dayjs from 'dayjs';
import {
  Box, Typography, Chip, Stack, Divider, IconButton, Tooltip, Collapse,
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import formatThaiDate from '../../utils/functions/formatThaiDate';
import { STATUS_GROUPS } from '../../utils/constants/financeStatus.constant';
import { PaymentTable } from './PaymentRow';
import DirectExpensesTable from './DirectExpensesTable';
import ApproverModal from '../Modal/ApproverModal';

// ─── Level 1: Date row ───────────────────────────────────────────────────────
export default function DateRow({ dateKey, paymentsOnDate, orphanExpenseIds = [] }) {
  const [open, setOpen] = useState(false);
  const [expOpen, setExpOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalExpenseIds, setModalExpenseIds] = useState([]);
  const [modalTitle, setModalTitle] = useState('');
  const [modalGroupStatuses, setModalGroupStatuses] = useState(null);

  const total = _.sumBy(paymentsOnDate, 'totalAmount');
  const allDone = paymentsOnDate.length > 0 && paymentsOnDate.every((p) => p.status === 'SUCCESS');

  const todayKey = dayjs().format('YYYY-MM-DD');
  const isToday = dateKey === todayKey;

  const expenseRows = useSelector((state) => state.expenses?.rows || []);
  const expenseMap = React.useMemo(
    () => _.keyBy(expenseRows, (e) => String(e._id)),
    [expenseRows],
  );

  // expenses ของ DateRow นี้ — กลุ่มตาม expense's own date เสมอ (ส่งมาจาก FinanceSummary)
  // ไม่ดึงจาก fromPayments เพื่อให้แต่ละ expense อยู่ใน DateRow ของวันตัวเองเท่านั้น
  const allExpenseIds = React.useMemo(() => [...new Set(orphanExpenseIds)], [orphanExpenseIds]);

  // วันนี้ → โชว์ทุก status / วันก่อนหน้า → ตัด SUCCESS ออก
  const displayExpenses = React.useMemo(() => {
    const allExps = allExpenseIds.map((id) => expenseMap[id]).filter(Boolean);
    if (isToday) return allExps;
    return allExps.filter((e) => e.status !== 'SUCCESS');
  }, [allExpenseIds, expenseMap, isToday]);

  const idsByGroup = React.useMemo(() => {
    const result = {};
    STATUS_GROUPS.forEach((g) => {
      result[g.key] = allExpenseIds.filter((id) => g.statuses.includes(expenseMap[id]?.status));
    });
    result.ALL = allExpenseIds;
    return result;
  }, [allExpenseIds, expenseMap]);

  const openModal = (ids, title, groupStatuses = null) => {
    setModalExpenseIds(ids);
    setModalTitle(title);
    setModalGroupStatuses(groupStatuses);
    setModalOpen(true);
  };

  const clickableStat = (label, ids, color, hover, groupStatuses = null) => (
    <Tooltip title={`คลิกเพื่อดู${label}`} placement="top">
      <Box
        onClick={(e) => { e.stopPropagation(); openModal(ids, label, groupStatuses); }}
        sx={{
          textAlign: 'center',
          cursor: 'pointer',
          px: 1,
          py: 0.5,
          borderRadius: 1,
          '&:hover': { backgroundColor: hover },
        }}
      >
        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
          {label}
        </Typography>
        <Typography fontWeight={700} fontSize="0.85rem" color={color}>
          {ids.length} รายการ
        </Typography>
      </Box>
    </Tooltip>
  );

  return (
    <Box
      sx={{
        mb: 1.5,
        borderRadius: 2,
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        backgroundColor: '#fff',
        boxShadow: open ? '0 2px 8px rgba(37,99,235,0.10)' : '0 1px 3px rgba(0,0,0,0.04)',
        transition: 'box-shadow 0.2s',
      }}
    >
      {/* Header */}
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          borderLeft: open ? '4px solid #2563eb' : '4px solid transparent',
          backgroundColor: open ? '#eff6ff' : '#fff',
          transition: 'background-color 0.15s, border-left-color 0.15s',
          '&:hover': { backgroundColor: open ? '#eff6ff' : '#f8faff' },
        }}
      >
        <IconButton size="small" sx={{ mr: 1, color: open ? '#2563eb' : 'text.secondary' }} disableRipple>
          {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>

        {/* Left: date */}
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label="วันที่รับเงิน" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, backgroundColor: '#dbeafe', color: '#1d4ed8', borderRadius: 1, '& .MuiChip-label': { px: 0.8 } }} />
            <Typography fontWeight={700} fontSize="0.95rem">
              {formatThaiDate(dateKey)}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {paymentsOnDate.length > 0 ? `${paymentsOnDate.length} payments` : 'ยังไม่มี payment'}
          </Typography>
        </Box>

        {/* Right: stats */}
        <Stack direction="row" alignItems="center" spacing={1.5} onClick={(e) => e.stopPropagation()}>
          {/* ทั้งหมด */}
          {clickableStat('ทั้งหมด', idsByGroup.ALL, 'text.primary', '#f3f4f6', null)}

          <Divider orientation="vertical" flexItem />

          {/* ยอดรวม (ไม่ clickable) */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
              ยอดรวม
            </Typography>
            <Typography fontWeight={700} fontSize="0.85rem" color="primary.dark">
              {total?.toLocaleString('th-TH', { minimumFractionDigits: 2 })} ฿
            </Typography>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* per-status */}
          {STATUS_GROUPS.map((g, i) => (
            <React.Fragment key={g.key}>
              {clickableStat(g.label, idsByGroup[g.key], g.color, g.hover, g.statuses)}
              {i < STATUS_GROUPS.length - 1 && <Divider orientation="vertical" flexItem />}
            </React.Fragment>
          ))}

          <Divider orientation="vertical" flexItem />

          <Chip
            label={allDone ? 'เสร็จสิ้น' : 'ดำเนินการอยู่'}
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: '0.7rem',
              minWidth: 90,
              backgroundColor: allDone ? '#dcfce7' : '#fff3cd',
              color: allDone ? '#15803d' : '#92400e',
            }}
          />
        </Stack>
      </Box>

      {/* Children */}
      <Collapse in={open}>
        {/* EXPENSE LIST */}
        <Box sx={{ borderTop: '2px solid #bbf7d0', backgroundColor: '#f0fdf4' }}>
          <Box
            onClick={() => setExpOpen((v) => !v)}
            sx={{
              px: 2, py: 0.6, backgroundColor: expOpen ? '#86efac' : '#bbf7d0',
              display: 'flex', alignItems: 'center', gap: 1,
              cursor: 'pointer', userSelect: 'none',
              '&:hover': { backgroundColor: '#86efac' },
              transition: 'background-color 0.15s',
            }}
          >
            <Box sx={{ width: 3, height: 14, borderRadius: 1, backgroundColor: '#15803d' }} />
            <ReceiptLongIcon sx={{ fontSize: 13, color: '#15803d' }} />
            <Typography variant="caption" fontWeight={700} color="#166534" sx={{ letterSpacing: 0.5, flexGrow: 1 }}>
              EXPENSE LIST
            </Typography>
            <Chip
              label={`${displayExpenses.length} รายการ`}
              size="small"
              sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, backgroundColor: '#dcfce7', color: '#15803d', '& .MuiChip-label': { px: 0.8 } }}
            />
            <IconButton size="small" disableRipple sx={{ color: '#15803d', p: 0, ml: 0.5 }}>
              {expOpen ? <KeyboardArrowDownIcon sx={{ fontSize: 16 }} /> : <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Box>
          <Collapse in={expOpen}>
            <DirectExpensesTable expenses={displayExpenses} />
          </Collapse>
        </Box>

        {/* PAYMENT DOCUMENTS */}
        <Box sx={{ borderTop: '2px solid #bfdbfe', backgroundColor: '#f0f7ff' }}>
          <Box
            onClick={() => setPayOpen((v) => !v)}
            sx={{
              px: 2, py: 0.6, backgroundColor: payOpen ? '#93c5fd' : '#bfdbfe',
              display: 'flex', alignItems: 'center', gap: 1,
              cursor: 'pointer', userSelect: 'none',
              '&:hover': { backgroundColor: '#93c5fd' },
              transition: 'background-color 0.15s',
            }}
          >
            <Box sx={{ width: 3, height: 14, borderRadius: 1, backgroundColor: '#2563eb' }} />
            <Typography variant="caption" fontWeight={700} color="#1e40af" sx={{ letterSpacing: 0.5, flexGrow: 1 }}>
              PAYMENT DOCUMENTS
            </Typography>
            <Chip
              label={`${paymentsOnDate.length} รายการ`}
              size="small"
              sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, backgroundColor: '#dbeafe', color: '#1d4ed8', '& .MuiChip-label': { px: 0.8 } }}
            />
            <IconButton size="small" disableRipple sx={{ color: '#2563eb', p: 0, ml: 0.5 }}>
              {payOpen ? <KeyboardArrowDownIcon sx={{ fontSize: 16 }} /> : <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Box>
          <Collapse in={payOpen}>
            <PaymentTable paymentsOnDate={paymentsOnDate} />
          </Collapse>
        </Box>
      </Collapse>

      {/* Modal */}
      <ApproverModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        expenseIds={modalExpenseIds}
        title={modalTitle}
        datePayment={dateKey}
        allowedStatuses={modalGroupStatuses}
      />
    </Box>
  );
}
